import { supabaseAdmin } from '@/lib/supabaseClient'
import type { EquipmentAssetType } from '@/types/inventory'

const BUCKET = 'equipment-assets'

export interface UploadResult {
  path: string
  publicUrl: string
}

async function ensureBucketExists() {
  const { data, error } = await supabaseAdmin.storage.listBuckets()
  if (error) throw error
  if (!data?.some((bucket) => bucket.name === BUCKET)) {
    const { error: createError } = await supabaseAdmin.storage.createBucket(BUCKET, {
      public: true,
      fileSizeLimit: 50 * 1024 * 1024,
    })
    if (createError) throw createError
  }
}

export async function uploadEquipmentAsset(options: {
  typeKey: string
  assetType: EquipmentAssetType
  file: File
}): Promise<UploadResult> {
  await ensureBucketExists()
  const objectKey = buildPath(options.typeKey, options.assetType, options.file.name)
  const { error } = await supabaseAdmin.storage.from(BUCKET).upload(objectKey, options.file, {
    cacheControl: '3600',
    upsert: true,
    contentType: options.file.type || 'application/octet-stream',
  })
  if (error) throw error
  const { data } = supabaseAdmin.storage.from(BUCKET).getPublicUrl(objectKey)
  return {
    path: objectKey,
    publicUrl: data?.publicUrl ?? '',
  }
}

export async function deleteEquipmentAsset(path: string) {
  await ensureBucketExists()
  const { error } = await supabaseAdmin.storage.from(BUCKET).remove([path])
  if (error) throw error
}

function buildPath(typeKey: string, assetType: EquipmentAssetType, filename: string) {
  const safeType = (typeKey || 'unclassified').replace(/[^a-z0-9_-]/gi, '-').toLowerCase()
  const safeName = filename.replace(/[^a-z0-9_.-]/gi, '-').toLowerCase()
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `${safeType}/${assetType}/${timestamp}-${safeName}`
}
